import { DisplaySlot, SynergyBonus, ArtifactInstance, ArtifactDef, GAME_CONFIG } from '../types';
import { getArtifactDef } from '../data/artifacts';

const UPGRADE_COSTS: Record<string, number> = {
  lighting: 2000,
  interactive: 5000,
  tour: 3000,
  gift_shop: 4000,
};

const VALID_UPGRADES = new Set(Object.keys(UPGRADE_COSTS));

export class Museum {
  slots: DisplaySlot[] = [];
  upgrades: Set<string> = new Set();
  totalVisitors: number = 0;

  constructor() {
    this.initSlots(6);
  }

  private initSlots(count: number): void {
    const cols = 3;
    for (let i = 0; i < count; i++) {
      this.slots.push({
        index: i,
        artifactUid: null,
        x: i % cols,
        y: Math.floor(i / cols),
      });
    }
  }

  placeArtifact(artifactUid: string, slotIndex: number): boolean {
    const slot = this.slots[slotIndex];
    if (!slot || slot.artifactUid !== null) {
      return false;
    }
    slot.artifactUid = artifactUid;
    return true;
  }

  removeArtifact(slotIndex: number): string | null {
    const slot = this.slots[slotIndex];
    if (!slot || slot.artifactUid === null) {
      return null;
    }
    const uid = slot.artifactUid;
    slot.artifactUid = null;
    return uid;
  }

  moveArtifact(fromSlot: number, toSlot: number): boolean {
    const from = this.slots[fromSlot];
    const to = this.slots[toSlot];
    if (!from || !to || from.artifactUid === null || to.artifactUid !== null) {
      return false;
    }
    to.artifactUid = from.artifactUid;
    from.artifactUid = null;
    return true;
  }

  calculateSynergies(
    getArtifactInstance: (uid: string) => ArtifactInstance | undefined
  ): SynergyBonus[] {
    const synergies: SynergyBonus[] = [];
    const occupied = this.getOccupiedSlots();

    // Helper: resolve instance + def for an occupied slot
    const resolve = (slot: DisplaySlot) => {
      if (!slot.artifactUid) return undefined;
      const inst = getArtifactInstance(slot.artifactUid);
      if (!inst) return undefined;
      const def = getArtifactDef(inst.defId);
      if (!def) return undefined;
      return { slot, inst, def, uid: slot.artifactUid };
    };

    const resolved = occupied.map(resolve).filter(
      (r): r is NonNullable<typeof r> => r !== undefined
    );

    // ── Showpiece ──
    const rareOrAbove: Set<string> = new Set(['rare', 'legendary', 'mythical']);
    const showpieceConditions: Set<string> = new Set(['pristine', 'good']);

    for (const r of resolved) {
      if (rareOrAbove.has(r.def.rarity) && showpieceConditions.has(r.inst.condition)) {
        synergies.push({
          type: 'showpiece',
          name: `Showpiece: ${r.def.name}`,
          multiplier: 1.5,
          artifactUids: [r.uid],
        });
      }
    }

    // ── Cultural Collection ──
    // Group by era+culture, then check adjacency
    const cultureGroups = new Map<string, typeof resolved>();
    for (const r of resolved) {
      const key = `${r.def.era}|${r.def.culture}`;
      if (!cultureGroups.has(key)) {
        cultureGroups.set(key, []);
      }
      cultureGroups.get(key)!.push(r);
    }

    for (const [key, group] of cultureGroups) {
      if (group.length < 2) continue;
      // Find connected clusters of adjacent artifacts in this group
      const adjacentUids = new Set<string>();
      for (let i = 0; i < group.length; i++) {
        for (let j = i + 1; j < group.length; j++) {
          const dx = Math.abs(group[i].slot.x - group[j].slot.x);
          const dy = Math.abs(group[i].slot.y - group[j].slot.y);
          if (dx <= 1 && dy <= 1) {
            adjacentUids.add(group[i].uid);
            adjacentUids.add(group[j].uid);
          }
        }
      }
      if (adjacentUids.size >= 2) {
        const [era, culture] = key.split('|');
        synergies.push({
          type: 'cultural_collection',
          name: `Cultural Collection: ${culture} ${era}`,
          multiplier: 1.3,
          artifactUids: Array.from(adjacentUids),
        });
      }
    }

    // ── Narrative Display ──
    // Group by site prefix extracted from storyFragmentId
    const narrativeGroups = new Map<string, typeof resolved>();
    for (const r of resolved) {
      const fragId = r.def.storyFragmentId;
      if (!fragId) continue;
      // Site prefix is everything before the last underscore+digits segment
      // e.g. "dc_frag_01" -> site prefix "dc"
      const parts = fragId.split('_frag_');
      const sitePrefix = parts[0];
      if (!sitePrefix) continue;
      if (!narrativeGroups.has(sitePrefix)) {
        narrativeGroups.set(sitePrefix, []);
      }
      narrativeGroups.get(sitePrefix)!.push(r);
    }

    for (const [sitePrefix, group] of narrativeGroups) {
      if (group.length < 2) continue;
      synergies.push({
        type: 'narrative_display',
        name: `Narrative Display: ${sitePrefix}`,
        multiplier: 1.25,
        artifactUids: group.map((r) => r.uid),
      });
    }

    return synergies;
  }

  getVisitorSatisfaction(
    getArtifactInstance: (uid: string) => ArtifactInstance | undefined
  ): number {
    let satisfaction = 20;

    // Appeal contribution
    const totalAppeal = this.getTotalAppeal(getArtifactInstance);
    const maxPossibleAppeal = this.slots.length * 10; // max museumAppeal is 10
    if (maxPossibleAppeal > 0) {
      satisfaction += (totalAppeal / maxPossibleAppeal) * 40;
    }

    // Synergy contribution
    const synergies = this.calculateSynergies(getArtifactInstance);
    satisfaction += synergies.length * 5;

    // Upgrade bonuses
    if (this.upgrades.has('lighting')) satisfaction += 10;
    if (this.upgrades.has('interactive')) satisfaction += 10;
    if (this.upgrades.has('tour')) satisfaction += 5;
    if (this.upgrades.has('gift_shop')) satisfaction += 5;

    return Math.min(100, Math.round(satisfaction));
  }

  getDailyIncome(
    getArtifactInstance: (uid: string) => ArtifactInstance | undefined
  ): number {
    const occupiedCount = this.getOccupiedCount();
    if (occupiedCount === 0) return 0;

    const satisfaction = this.getVisitorSatisfaction(getArtifactInstance);
    const giftShopBonus = this.upgrades.has('gift_shop') ? 0.3 : 0;

    return Math.round(occupiedCount * 50 * (satisfaction / 100) * (1 + giftShopBonus));
  }

  getOccupiedCount(): number {
    return this.slots.filter((s) => s.artifactUid !== null).length;
  }

  getTotalAppeal(
    getArtifactInstance: (uid: string) => ArtifactInstance | undefined
  ): number {
    let total = 0;
    for (const slot of this.slots) {
      if (!slot.artifactUid) continue;
      const inst = getArtifactInstance(slot.artifactUid);
      if (!inst) continue;
      const def = getArtifactDef(inst.defId);
      if (!def) continue;
      total += def.museumAppeal;
    }
    return total;
  }

  expandSlots(count: number): void {
    const cols = 3;
    const startIndex = this.slots.length;
    for (let i = 0; i < count; i++) {
      const idx = startIndex + i;
      this.slots.push({
        index: idx,
        artifactUid: null,
        x: idx % cols,
        y: Math.floor(idx / cols),
      });
    }
  }

  purchaseUpgrade(upgrade: string): boolean {
    if (!VALID_UPGRADES.has(upgrade)) return false;
    if (this.upgrades.has(upgrade)) return false;
    this.upgrades.add(upgrade);
    return true;
  }

  getUpgradeCost(upgrade: string): number {
    return UPGRADE_COSTS[upgrade] ?? 0;
  }

  getExpansionCost(): number {
    return this.slots.length * 500;
  }

  getDailyUpkeep(): number {
    return this.getOccupiedCount() * 20 + this.upgrades.size * 30;
  }

  getOccupiedSlots(): DisplaySlot[] {
    return this.slots.filter((s) => s.artifactUid !== null);
  }
}
